import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { refillPeriod } from '@models/auto-topup';
import { User } from '@models/user';
import { TranslateService } from '@ngx-translate/core';
import { BillingTabManagerService } from '@services/billing-tab-manager.service';
import { LoaderService } from '@services/loader.service';
import { PaymentService } from '@services/payment.service';
import { ShopService } from '@services/shop.service';
import { UserService } from '@services/user.service';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-billing-auto-top-up',
  templateUrl: './billing-auto-top-up.component.html',
  styleUrls: ['./billing-auto-top-up.component.scss'],
  standalone: false
})
export class BillingAutoTopUpComponent implements OnInit, OnDestroy {
  @Input() user: User;
  autotopupForm: UntypedFormGroup = null;
  shopItemSMS: any = [];

  autotopUpLimitCheck = false;
  autotopupCongig;
  itemSelected;
  itemSelectedBackUp;
  refillPeriodEnum = refillPeriod;
  paymentMethod;

  submitForm: Subscription;
  resetForm: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private shopService: ShopService,
    private loaderService: LoaderService,
    private paymentService: PaymentService,
    private billingTabManagerService: BillingTabManagerService,
    private userService: UserService,
    private translateService: TranslateService
  ) {}
  ngOnDestroy(): void {
    this.submitForm.unsubscribe();
    this.resetForm.unsubscribe();
  }

  ngOnInit(): void {
    this.loaderService.show();
    this.initAutoTopUpTab();
    this.loadPaymentMethod();

    this.submitForm = this.billingTabManagerService.save$.subscribe(() => {
      this.submitAutoTopUpForm();
    });

    this.resetForm = this.billingTabManagerService.resetForm$.subscribe(() => {
      this.autotopupForm.reset(this.autotopupCongig);
      this.itemSelected = this.itemSelectedBackUp;
      if (this.autotopupForm.get('idShopItem').value === '') {
        this.autotopupForm.get('idShopItem').clearValidators();
        this.autotopupForm.get('idShopItem').updateValueAndValidity();
      }
    });
  }

  initAutoTopUpTab() {
    this.autotopupForm = this.formBuilder.group({
      idShopItem: [''],
      idCustShopItem: [''],
      enabled: [false, [Validators.required]],
      creditType: ['eur', [Validators.required]],
      alertLimit: [100, [Validators.required]],
      smsType: ['GP', [Validators.required]],
      maxRefillPerPeriod: [1],
      refillPeriod: ['DAY'],
      purchaseOrderNumber: [''],
      agreeCheck: [false, Validators.requiredTrue]
    });

    this.shopService.getItems(this).subscribe(shopItems => {
      if (shopItems.data.length) {
        for (const item of shopItems.data) {
          if (item.label === 'list-sms-shop-items') {
            this.shopItemSMS = this.shopItemSMS.concat(item.items);
          }
          if (item.label === 'list-recurrent-shop-items') {
            if (item.items.length) {
              for (const subitem of item.items) {
                if (subitem.category === 'cust_pck') {
                  this.shopItemSMS.push(subitem);
                }
              }
            }
          }
        }
      }
      this.loadAutoTopUpConfig();
    });
  }

  loadAutoTopUpConfig() {
    this.paymentService
      .getAutoTopUpConfig()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(data => {
        const merged = _.merge(this.autotopupForm.getRawValue(), data);
        if (!_.isUndefined(data.enabled)) {
          merged.agreeCheck = true;
          if (!_.isUndefined(data.maxRefillPerPeriod)) {
            this.autotopUpLimitCheck = true;
          }
        }

        this.autotopupCongig = merged;
        this.autotopupForm.patchValue(this.autotopupCongig);
        this.autotopUpEnableLimitCheck();
        if (this.autotopupForm.get('idShopItem').value.length) {
          this.autotopupForm.get('agreeCheck').setValue(true);
          this.itemSelected = this.doesItemExist(this.autotopupForm.get('idShopItem').value);
          this.itemSelectedBackUp = this.itemSelected;
        } else if (this.autotopupForm.get('idCustShopItem').value.length) {
          this.autotopupForm.get('agreeCheck').setValue(true);
          this.itemSelected = this.doesItemExist(this.autotopupForm.get('idCustShopItem').value);
          this.itemSelectedBackUp = this.itemSelected;
        } else {
          this.selectItem(this.shopItemSMS[0].idShopItem);
          this.itemSelected = this.shopItemSMS[0].idShopItem;
          this.itemSelectedBackUp = this.itemSelected;
        }

        this.autotopupForm.valueChanges.subscribe(() => {
          this.billingTabManagerService.allowChangeTab(this.autotopupForm);
          this.billingTabManagerService.disableSave(this.autotopupForm);
        });
      });
  }

  doesItemExist(idShopItem) {
    let item = this.shopItemSMS.find(item => item.idShopItem === idShopItem);
    if (!item) {
      this.autotopupForm.get('enabled').setValue(false);
      return this.shopItemSMS[0].idShopItem;
    }
    return item;
  }

  selectItem(idItem) {
    const item = this.shopItemSMS.find(x => x.idShopItem === idItem.idShopItem);
    if (item?.customItem) {
      this.autotopupForm.get('idCustShopItem').setValue(item?.idShopItem, { emitEvent: false });
      this.autotopupForm.get('idShopItem').setValue('', { emitEvent: false });
    } else {
      this.autotopupForm.get('idShopItem').setValue(item?.idShopItem, { emitEvent: false });
      this.autotopupForm.get('idCustShopItem').setValue('', { emitEvent: false });
    }
    this.autotopupForm.markAsDirty();

    this.enableIdShopItemValidator(item?.customItem);
  }

  loadPaymentMethod(): void {
    this.paymentService
      .getPaymentMethod()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(
        data => {
          this.paymentMethod = data;
        },
        () => {}
      );
  }

  autotopUpEnableLimitCheck() {
    if (!this.autotopUpLimitCheck) {
      this.autotopupForm.get('maxRefillPerPeriod').disable();
      this.autotopupForm.get('refillPeriod').disable();
    } else {
      this.autotopupForm.get('maxRefillPerPeriod').enable();
      this.autotopupForm.get('refillPeriod').enable();
    }
  }

  enableAutoTopUp(ev) {
    if (ev.checked) {
      this.autotopupForm.get('agreeCheck').setValidators([Validators.requiredTrue]);
      if (this.autotopupForm.get('idCustShopItem').value === '') {
        this.autotopupForm.get('idShopItem').setValidators([Validators.required]);
      }
    } else {
      this.autotopupForm.get('agreeCheck').clearValidators();
      this.autotopupForm.get('idShopItem').clearValidators();
    }
    this.autotopupForm.get('agreeCheck').updateValueAndValidity();
    this.autotopupForm.get('idShopItem').updateValueAndValidity();
  }

  submitAutoTopUpForm() {
    if (!_.isNull(this.paymentMethod)) {
      const value = _.cloneDeep(this.autotopupForm.getRawValue());

      Object.keys(value).forEach(key => value[key] === '' && delete value[key]);
      Object.keys(value).forEach(key => value[key] === null && delete value[key]);

      if (!this.autotopUpLimitCheck) {
        delete value.refillPeriod;
        delete value.maxRefillPerPeriod;
      }

      delete value.agreeCheck;
      value.enableCreditAutoRefill = true;
      this.paymentService.editAutoTopUpConfig(value).subscribe(
        savedUser => {
          this.autotopupCongig = this.autotopupForm.getRawValue();
          this.autotopupForm.reset(this.autotopupCongig);
          this.billingTabManagerService.successMessage('common_success');
          this.autotopupForm.enable();
          this.itemSelectedBackUp = this.itemSelected;
        },
        error => {
          this.billingTabManagerService.errorMessage('common_error');
          this.autotopupForm.enable();
        }
      );
    } else {
      this.billingTabManagerService.errorMessage('common_error_cart');
    }
  }

  private enableIdShopItemValidator(customItem) {
    if (customItem) {
      this.autotopupForm.get('idShopItem').clearValidators();
    } else {
      this.autotopupForm.get('idShopItem').setValidators([Validators.required]);
    }
    this.autotopupForm.get('idShopItem').updateValueAndValidity();
  }

  getUserBeneficiary(data?: any) {
    if (data) {
      const userBeneficiary = data.userBeneficiary;
      if (userBeneficiary) {
        if (userBeneficiary.idUserBeneficiary !== this.userService.getSessionUserId()) {
          return this.styleCellBeneficiary(userBeneficiary);
        }
      }
    }
  }

  private styleCellBeneficiary(userBeneficiary): string {
    return (
      '<div style="font-weight: bold;">' +
      userBeneficiary?.loginBeneficiary +
      '</div>' +
      userBeneficiary?.nameBeneficiary +
      ' ' +
      userBeneficiary?.surnameBeneficiary +
      '<br>' +
      userBeneficiary?.companyBeneficiary
    );
  }

  replaceLabelTagWithValue(item) {
    let label: string = item.label ? this.translateService.instant(item.label) : '';

    const splitChart = this.user.nation === 'gbr' ? '.' : ',';

    label = label.replace('$1$', item?.serviceParams?.ratedSms);
    label = item.price ? label.replace('$2$', item.price.toString().split(splitChart)[0]) : label;

    return label;
  }
}
